import { Timestamp } from 'firebase/firestore';

// Currency enum with supported currencies
export enum Currency {
  USD = "USD",  // US Dollar
  EUR = "EUR",  // Euro
  MXN = "MXN"   // Mexican Peso
}

// Fuel unit enum for fuel price measurement
export enum FuelUnit {
  LITER = "LITER",
  GALLON = "GALLON"
}

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  tokens?: number;
  isAdmin?: boolean;
  lastTokenUpdate?: Date;
}

export interface QuotationData {
  id?: string;
  userId: string;
  origin: string;
  destination: string;
  distance: number;
  fuelPrice: number;
  fuelPriceUnit?: FuelUnit; // Unit of measurement for fuel price (liters or gallons)
  vehicleType: VehicleType;
  trailerType: TrailerType | null;
  totalCost: number;
  profitMargin?: number; // Porcentaje de utilidad
  profitAmount?: number; // Monto de utilidad
  baseCost?: number; // Costo base sin utilidad
  cargoType?: CargoType; // Tipo de mercancía
  riskFactor?: number; // Factor de riesgo aplicado
  riskAmount?: number; // Monto adicional por factor de riesgo
  createdAt: Date;
  mapImageUrl?: string;
  originAddress?: string;
  destinationAddress?: string;
  costBreakdown?: Record<string, number>;
  tollDetails?: TollDetails;
  selectedCurrency?: Currency;
  isMultiSegmentRoute?: boolean;
  segmentDistances?: number[];
  segmentDurations?: string[];
  segmentLoadStatus?: ('loaded' | 'empty')[];
  waypoints?: string[];
  capturedMapImage?: string;
}

export interface TokenTransaction {
  id?: string;
  userId: string;
  amount: number;
  type: TokenTransactionType;
  description: string;
  createdAt: Date;
  createdBy?: string;
  checkoutSessionId?: string;
}

export enum TokenTransactionType {
  USED = "used",
  ADDED = "added",
  PURCHASED = "purchased",
  INITIAL = "initial"
}

export interface TokenPackage {
  id: string;
  name: string;
  tokens: number;
  price: number;
  description: string;
  isPopular?: boolean;
  translationKey: string;
}

export interface TollDetails {
  tollStops: TollStop[];
  totalTollCost: number;
  lastUpdated: Date;
  tollCurrency?: Currency; // New field for the original currency of the tolls
}

export interface TollStop {
  name: string;
  location: MapLocation;
  cost: number;
  distance: number;
  operatedBy: string;
  paymentMethods: PaymentMethod[];
}

export enum PaymentMethod {
  CASH = "Efectivo",
  CARD = "Tarjeta",
  TAG = "TAG",
  IAVE = "IAVE"
}

export enum VehicleType {
  PICKUP_TRUCK = "Camioneta de Carga Sencilla",
  DUAL_WHEEL_PICKUP = "Camioneta de Carga con Doble Rodado",
  RABON = "Rabón",
  TORTON = "Torton",
  SINGLE_WITH_2_AXLE_TRAILER = "Unidad en sencillo con un Remolque de 2 ejes",
  SINGLE_WITH_3_AXLE_TRAILER = "Unidad en sencillo con un Remolque de 3 ejes",
  FULL_6_AXLES = "Unidad en Full con 6 ejes en total",
  FULL_7_AXLES = "Unidad en Full con 7 ejes en total",
  FULL_8_AXLES = "Unidad en Full con 8 ejes en total",
  FULL_9_AXLES = "Unidad en Full con 9 ejes en total"
}

export enum TrailerType {
  DRY_BOX = "Caja Seca",
  REFRIGERATED_BOX = "Caja Refrigerada",
  PLATFORM = "Plataforma",
  CONTAINER_CARRIER = "Porta Contenedor",
  GRAIN_CAGE = "Jaula Granelera",
  GRAIN_HOPPER = "Tolva Granelera",
  PRESSURIZED_HOPPER = "Tolva Presurizada",
  LIQUID_TANK = "Tanque de Líquidos",
  LOWBOY = "LowBoy o Cama Baja"
}

export enum CargoType {
  GENERAL_MERCHANDISE = "Mercancía General",
  PERISHABLE_FOOD = "Alimentos Perecederos",
  NON_PERISHABLE_FOOD = "Alimentos No Perecederos",
  CONSTRUCTION_MATERIALS = "Materiales de Construcción",
  AGRICULTURAL_PRODUCTS = "Productos Agrícolas",
  FURNITURE = "Mobiliario",
  CLOTHING_TEXTILES = "Ropa y Textiles",
  ELECTRONICS = "Productos Electrónicos",
  AUTOMOTIVE_PARTS = "Partes Automotrices",
  HOUSEHOLD_APPLIANCES = "Electrodomésticos",
  STEEL_METALS = "Acero y Metales",
  CHEMICALS = "Productos Químicos",
  HAZARDOUS_MATERIALS = "Materiales Peligrosos",
  FRAGILE_ITEMS = "Productos Frágiles",
  HIGH_VALUE_GOODS = "Mercancía de Alto Valor",
  MACHINERY = "Maquinaria",
  LIVESTOCK = "Ganado",
  PHARMACEUTICALS = "Productos Farmacéuticos",
  WOOD_PRODUCTS = "Productos de Madera",
  OTHER = "Otros"
}

// Categorizaciones de riesgo predefinidas por tipo de carga
export const CARGO_RISK_FACTORS: Record<CargoType, number> = {
  [CargoType.GENERAL_MERCHANDISE]: 1.00,
  [CargoType.PERISHABLE_FOOD]: 1.30,
  [CargoType.NON_PERISHABLE_FOOD]: 1.10,
  [CargoType.CONSTRUCTION_MATERIALS]: 1.05,
  [CargoType.AGRICULTURAL_PRODUCTS]: 1.15,
  [CargoType.FURNITURE]: 1.25,
  [CargoType.CLOTHING_TEXTILES]: 1.08,
  [CargoType.ELECTRONICS]: 1.40,
  [CargoType.AUTOMOTIVE_PARTS]: 1.15,
  [CargoType.HOUSEHOLD_APPLIANCES]: 1.25,
  [CargoType.STEEL_METALS]: 1.05,
  [CargoType.CHEMICALS]: 1.50,
  [CargoType.HAZARDOUS_MATERIALS]: 1.80,
  [CargoType.FRAGILE_ITEMS]: 1.45,
  [CargoType.HIGH_VALUE_GOODS]: 1.60,
  [CargoType.MACHINERY]: 1.30,
  [CargoType.LIVESTOCK]: 1.45,
  [CargoType.PHARMACEUTICALS]: 1.40,
  [CargoType.WOOD_PRODUCTS]: 1.05,
  [CargoType.OTHER]: 1.20
};

export interface CargoRiskData {
  riskFactor: number;
  description: string;
  securityRecommendations: string[];
}

export const CARGO_RISK_DETAILS: Record<CargoType, CargoRiskData> = {
  [CargoType.GENERAL_MERCHANDISE]: {
    riskFactor: 1.00,
    description: "Mercancía general de riesgo estándar que no requiere condiciones especiales de transporte.",
    securityRecommendations: [
      "Embalaje adecuado para proteger contra golpes leves",
      "Asegurar carga con correas estándar",
      "Utilizar lonas impermeables durante temporada de lluvias"
    ]
  },
  [CargoType.PERISHABLE_FOOD]: {
    riskFactor: 1.30,
    description: "Alimentos que requieren refrigeración y tienen fecha de caducidad corta. Riesgo alto por necesidad de control de temperatura y tiempo de entrega.",
    securityRecommendations: [
      "Transporte en unidades con equipo de refrigeración calibrado",
      "Monitoreo constante de temperatura durante todo el trayecto",
      "Prioridad en rutas para minimizar tiempo de tránsito",
      "Plan de contingencia para fallas en sistema de refrigeración"
    ]
  },
  [CargoType.NON_PERISHABLE_FOOD]: {
    riskFactor: 1.10,
    description: "Alimentos con vida útil prolongada pero que requieren protección contra humedad y plagas.",
    securityRecommendations: [
      "Protección contra humedad y cambios de temperatura",
      "Embalaje resistente a roedores e insectos",
      "Verificación periódica de sellos y empaques"
    ]
  },
  [CargoType.CONSTRUCTION_MATERIALS]: {
    riskFactor: 1.05,
    description: "Materiales resistentes con bajo riesgo de daño durante el transporte.",
    securityRecommendations: [
      "Distribución adecuada del peso en la unidad",
      "Protección contra lluvia para materiales sensibles a humedad",
      "Asegurar correctamente para evitar desplazamientos"
    ]
  },
  [CargoType.AGRICULTURAL_PRODUCTS]: {
    riskFactor: 1.15,
    description: "Productos agrícolas susceptibles a daños por manipulación y condiciones climáticas.",
    securityRecommendations: [
      "Ventilación adecuada para productos frescos",
      "Evitar exposición directa al sol",
      "Separación entre productos para prevenir contaminación cruzada"
    ]
  },
  [CargoType.FURNITURE]: {
    riskFactor: 1.25,
    description: "Mobiliario vulnerable a rayones, abolladuras y roturas durante el transporte.",
    securityRecommendations: [
      "Embalaje protector en esquinas y superficies delicadas",
      "Uso de separadores entre piezas",
      "Asegurar firmemente para evitar movimiento durante el transporte",
      "Manejo cuidadoso en carga y descarga"
    ]
  },
  [CargoType.CLOTHING_TEXTILES]: {
    riskFactor: 1.08,
    description: "Productos textiles con riesgo moderado de daños por humedad o contaminantes.",
    securityRecommendations: [
      "Protección impermeable contra líquidos",
      "Embalaje que evite manchas y olores",
      "Asegurar que el área de carga esté limpia"
    ]
  },
  [CargoType.ELECTRONICS]: {
    riskFactor: 1.40,
    description: "Equipos electrónicos de alto valor y fragilidad, sensibles a golpes y condiciones ambientales.",
    securityRecommendations: [
      "Embalaje con protección antiestática",
      "Material amortiguador para absorber impactos",
      "Control de temperatura y humedad",
      "Seguridad reforzada contra robo",
      "Evitar exposición a campos electromagnéticos fuertes"
    ]
  },
  [CargoType.AUTOMOTIVE_PARTS]: {
    riskFactor: 1.15,
    description: "Componentes automotrices que pueden incluir piezas pesadas y de precisión.",
    securityRecommendations: [
      "Embalaje resistente para piezas pesadas",
      "Protección anticorrosión para metales expuestos",
      "Identificación clara de piezas frágiles o de precisión"
    ]
  },
  [CargoType.HOUSEHOLD_APPLIANCES]: {
    riskFactor: 1.25,
    description: "Electrodomésticos con componentes frágiles y superficies susceptibles a daños.",
    securityRecommendations: [
      "Embalaje original del fabricante cuando sea posible",
      "Refuerzo adicional en esquinas y bordes",
      "Sujeción que evite cualquier movimiento dentro del vehículo",
      "Manipulación por personal capacitado"
    ]
  },
  [CargoType.STEEL_METALS]: {
    riskFactor: 1.05,
    description: "Productos metálicos resistentes pero pesados que requieren consideraciones de distribución de peso.",
    securityRecommendations: [
      "Distribución adecuada del peso en la plataforma",
      "Protección contra humedad para prevenir oxidación",
      "Sujeción reforzada para evitar desplazamientos"
    ]
  },
  [CargoType.CHEMICALS]: {
    riskFactor: 1.50,
    description: "Productos químicos que pueden ser corrosivos, inflamables o reactivos, requiriendo manejo especializado.",
    securityRecommendations: [
      "Cumplimiento estricto de normativas para transporte de químicos",
      "Separación adecuada entre productos incompatibles",
      "Equipo especializado y señalización según normas",
      "Personal con certificación para manejo de químicos",
      "Plan de respuesta a emergencias por derrames"
    ]
  },
  [CargoType.HAZARDOUS_MATERIALS]: {
    riskFactor: 1.80,
    description: "Materiales con clasificación de peligrosidad que requieren permisos especiales y protocolos estrictos.",
    securityRecommendations: [
      "Permisos y documentación especial obligatoria",
      "Unidades certificadas para transporte de materiales peligrosos",
      "Conductores con capacitación y certificación especial",
      "Rutas predeterminadas con evaluación de riesgos",
      "Coordinación con autoridades de emergencia",
      "Sistemas de contención secundaria para derrames"
    ]
  },
  [CargoType.FRAGILE_ITEMS]: {
    riskFactor: 1.45,
    description: "Artículos con alta fragilidad que requieren embalaje especializado y manejo cuidadoso.",
    securityRecommendations: [
      "Embalaje de alta protección con materiales amortiguadores",
      "Señalización clara de fragilidad en todos los empaques",
      "Carga separada de artículos pesados",
      "Verificaciones periódicas durante el trayecto",
      "Conducción suave evitando maniobras bruscas"
    ]
  },
  [CargoType.HIGH_VALUE_GOODS]: {
    riskFactor: 1.60,
    description: "Mercancía de alto valor que representa un riesgo elevado de robo y requiere medidas de seguridad intensivas.",
    securityRecommendations: [
      "Escoltas de seguridad durante todo el trayecto",
      "Vehículos con sistemas GPS y seguimiento en tiempo real",
      "Rutas variables y no predecibles",
      "Comunicación constante con base de operaciones",
      "Seguro especializado de alto valor",
      "Conductores verificados con antecedentes limpios"
    ]
  },
  [CargoType.MACHINERY]: {
    riskFactor: 1.30,
    description: "Equipos y maquinaria pesada con componentes sensibles y distribución de peso compleja.",
    securityRecommendations: [
      "Estudio previo de rutas para verificar capacidad de puentes y vías",
      "Permisos especiales para cargas sobredimensionadas",
      "Anclajes y sujeciones reforzados",
      "Protección especial para componentes hidráulicos y electrónicos",
      "Vehículos escolta para dimensiones especiales"
    ]
  },
  [CargoType.LIVESTOCK]: {
    riskFactor: 1.45,
    description: "Animales vivos que requieren consideraciones de bienestar animal, alimentación y descanso.",
    securityRecommendations: [
      "Vehículos acondicionados para transporte de animales",
      "Ventilación y temperatura controlada",
      "Paradas programadas para alimentación y descanso",
      "Personal capacitado en manejo de ganado",
      "Permisos sanitarios y de movilización",
      "Plan de contingencia para emergencias veterinarias"
    ]
  },
  [CargoType.PHARMACEUTICALS]: {
    riskFactor: 1.40,
    description: "Productos farmacéuticos sensibles a temperatura y que pueden requerir cadena de frío y seguridad adicional.",
    securityRecommendations: [
      "Control y registro continuo de temperatura",
      "Validación de equipos de refrigeración",
      "Embalaje térmico calificado",
      "Seguridad reforzada contra robo",
      "Documentación exhaustiva de la cadena de custodia",
      "Procedimientos para gestión de desviaciones de temperatura"
    ]
  },
  [CargoType.WOOD_PRODUCTS]: {
    riskFactor: 1.05,
    description: "Productos de madera relativamente resistentes pero que requieren protección contra humedad.",
    securityRecommendations: [
      "Protección contra lluvia y humedad excesiva",
      "Sujeción adecuada para evitar desplazamientos",
      "Protección en bordes para maderas finas o tratadas"
    ]
  },
  [CargoType.OTHER]: {
    riskFactor: 1.20,
    description: "Categoría general para mercancías que no encajan en otras clasificaciones, se aplica un factor de riesgo moderado.",
    securityRecommendations: [
      "Evaluación caso por caso del riesgo específico",
      "Embalaje adecuado según características del producto",
      "Documentación detallada del contenido",
      "Consideraciones especiales a determinar según la mercancía"
    ]
  }
};

export interface LogisticsTip {
  id: number;
  title: string;
  description: string;
  imageUrl: string;
  rarity: TipRarity;
}

export enum TipRarity {
  COMMON = 'Common',
  UNCOMMON = 'Uncommon',
  RARE = 'Rare',
  EPIC = 'Epic',
  LEGENDARY = 'Legendary'
}

export interface MapLocation {
  lat: number;
  lng: number;
}

export interface RouteInfo {
  origin: MapLocation | null;
  destination: MapLocation | null;
  distance: number;
  duration: string;
  originAddress?: string;
  destinationAddress?: string;
  isMultiSegmentRoute?: boolean;
  segmentDistances?: number[];
  segmentDurations?: string[];
  segmentLoadStatus?: ('loaded' | 'empty')[];
  encodedPolyline?: string;
}

export interface TollCache {
  id: string;
  origin: string;
  destination: string;
  vehicleType: VehicleType;
  tollDetails: TollDetails;
  createdAt: Timestamp;
  expiresAt: Timestamp;
}